import { useState } from 'react';
import { X, RotateCcw, HelpCircle, Search, Layers, Sparkles, MessageSquare, Shield, FileText, Wand2, Tag } from 'lucide-react';
import { useSettingsStore, DEFAULT_SETTINGS, RAGSettings } from '../../store/settingsStore';
import { GuardrailsPanel } from './GuardrailsPanel';
import { ReleaseNotesPanel } from './ReleaseNotesPanel';
import { PromptsPanel } from './PromptsPanel';
import { EntityTypesPanel } from './EntityTypesPanel';

interface SettingsModalProps {
  onClose: () => void;
}

type SettingGroup = 'search' | 'context' | 'generation' | 'troubleshooting' | 'safety' | 'prompts' | 'entityTypes' | 'releases';

interface SettingConfig {
  key: keyof RAGSettings;
  label: string;
  description: string;
  min: number;
  max: number;
  step: number;
  impact: 'high' | 'medium' | 'low';
  unit?: string;
}

const SETTING_GROUPS: Record<SettingGroup, { label: string; icon: typeof Search; settings: SettingConfig[] }> = {
  search: {
    label: 'Search Logic',
    icon: Search,
    settings: [
      {
        key: 'semanticWeight',
        label: 'Semantic Weight',
        description: 'Higher values prioritize meaning and intent. Best for natural language questions.',
        min: 0,
        max: 1,
        step: 0.1,
        impact: 'high',
      },
      {
        key: 'lexicalWeight',
        label: 'Lexical (BM25) Weight',
        description: 'Higher values prioritize exact keyword matches. Best for specific error codes or module names.',
        min: 0,
        max: 1,
        step: 0.1,
        impact: 'medium',
      },
    ],
  },
  context: {
    label: 'Context Control',
    icon: Layers,
    settings: [
      {
        key: 'topK',
        label: 'Context Window (Top-K)',
        description: 'Number of document chunks sent to the AI. Higher = more detail but slower.',
        min: 1,
        max: 20,
        step: 1,
        impact: 'high',
      },
      {
        key: 'confidenceThreshold',
        label: 'Confidence Threshold',
        description: 'Minimum score for a document to be used. Higher = more accurate but may say "I don\'t know" more often.',
        min: 0.3,
        max: 1,
        step: 0.05,
        impact: 'high',
      },
    ],
  },
  generation: {
    label: 'Generation Style',
    icon: Sparkles,
    settings: [
      {
        key: 'temperature',
        label: 'Temperature',
        description: 'Controls creativity. Low (0.1-0.3) = fact-based, High (0.7+) = creative but may invent steps.',
        min: 0,
        max: 1,
        step: 0.1,
        impact: 'medium',
      },
      {
        key: 'maxTokens',
        label: 'Max Tokens (Length)',
        description: 'Maximum response length. Higher for code samples, lower for quick summaries.',
        min: 256,
        max: 4096,
        step: 256,
        impact: 'low',
        unit: 'tokens',
      },
    ],
  },
  troubleshooting: {
    label: 'Troubleshooting',
    icon: MessageSquare,
    settings: [
      {
        key: 'maxDiagnosticTurns',
        label: 'Max Diagnostic Turns',
        description: 'Number of follow-up questions the AI will ask. More = better debugging, fewer = faster answers.',
        min: 1,
        max: 10,
        step: 1,
        impact: 'medium',
      },
    ],
  },
  safety: {
    label: 'Safety & Guardrails',
    icon: Shield,
    settings: [],  // Guardrails uses its own panel, not sliders
  },
  prompts: {
    label: 'LLM Prompts',
    icon: Wand2,
    settings: [],  // Prompts panel is fully custom
  },
  entityTypes: {
    label: 'Entity Types',
    icon: Tag,
    settings: [],  // Entity Types panel is fully custom
  },
  releases: {
    label: 'Release Notes',
    icon: FileText,
    settings: [],  // Release notes uses its own panel
  },
};

const IMPACT_COLORS = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700',
};

export function SettingsModal({ onClose }: SettingsModalProps) {
  const { settings, updateSettings, resetToDefaults } = useSettingsStore();
  const [activeGroup, setActiveGroup] = useState<SettingGroup>('search');
  const [showHelp, setShowHelp] = useState(false);

  const handleSliderChange = (key: keyof RAGSettings, value: number) => {
    updateSettings({ [key]: value });

    // Auto-balance semantic/lexical weights
    if (key === 'semanticWeight') {
      updateSettings({ lexicalWeight: Math.round((1 - value) * 10) / 10 });
    } else if (key === 'lexicalWeight') {
      updateSettings({ semanticWeight: Math.round((1 - value) * 10) / 10 });
    }
  };

  const formatValue = (value: number, step: number, unit?: string) => {
    const formatted = step < 1 ? value.toFixed(1) : value.toString();
    return unit ? `${formatted} ${unit}` : formatted;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-800">Performance & Response Settings</h2>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              title="Toggle help"
            >
              <HelpCircle size={18} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefaults}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Reset to defaults"
            >
              <RotateCcw size={14} />
              Reset
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Help Banner */}
        {showHelp && (
          <div className="p-4 bg-blue-50 border-b border-blue-100 text-sm text-blue-800">
            <p className="font-medium mb-2">How these settings affect responses:</p>
            <ul className="list-disc ml-5 space-y-1 text-blue-700">
              <li><strong>Search Logic:</strong> Controls how the system finds relevant documentation (meaning vs exact keywords)</li>
              <li><strong>Context Control:</strong> Manages how much information is provided to the AI</li>
              <li><strong>Generation Style:</strong> Affects how creative or factual the AI responses are</li>
              <li><strong>Troubleshooting:</strong> Controls the diagnostic conversation depth</li>
            </ul>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <nav className="w-48 border-r border-gray-200 p-2 flex-shrink-0">
            {(Object.entries(SETTING_GROUPS) as [SettingGroup, typeof SETTING_GROUPS[SettingGroup]][]).map(
              ([key, group]) => {
                const Icon = group.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveGroup(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeGroup === key
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={16} />
                    {group.label}
                  </button>
                );
              }
            )}
          </nav>

          {/* Settings Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeGroup === 'safety' ? (
              <GuardrailsPanel />
            ) : activeGroup === 'prompts' ? (
              <PromptsPanel />
            ) : activeGroup === 'entityTypes' ? (
              <EntityTypesPanel />
            ) : activeGroup === 'releases' ? (
              <ReleaseNotesPanel />
            ) : (
              <div className="space-y-6">
                {SETTING_GROUPS[activeGroup].settings.map((setting) => (
                  <div key={setting.key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <label className="font-medium text-gray-800">{setting.label}</label>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${IMPACT_COLORS[setting.impact]}`}
                        >
                          {setting.impact.charAt(0).toUpperCase() + setting.impact.slice(1)} Impact
                        </span>
                      </div>
                      <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {formatValue(settings[setting.key], setting.step, setting.unit)}
                      </span>
                    </div>

                    <input
                      type="range"
                      min={setting.min}
                      max={setting.max}
                      step={setting.step}
                      value={settings[setting.key]}
                      onChange={(e) => handleSliderChange(setting.key, parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                    />

                    <div className="flex justify-between text-xs text-gray-400">
                      <span>{formatValue(setting.min, setting.step)}</span>
                      <span>{formatValue(setting.max, setting.step, setting.unit)}</span>
                    </div>

                    <p className="text-sm text-gray-500">{setting.description}</p>

                    {/* Show default value indicator */}
                    {settings[setting.key] !== DEFAULT_SETTINGS[setting.key] && (
                      <p className="text-xs text-amber-600">
                        Default: {formatValue(DEFAULT_SETTINGS[setting.key], setting.step, setting.unit)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">
            Changes are saved automatically and applied to new queries.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
